import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '~/shared/api/data-ingestion-api'
import { GOLD_SET_QUERY_KEYS } from '../config'
import type { GoldSetDocument, GoldSetLabel, GoldSetLabelInput, GoldSetQueue } from '../model'

export function useSaveGoldSetLabel() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      documentKey,
      label,
    }: {
      documentKey: string
      label: GoldSetLabelInput
    }) => {
      const response = await apiClient.client.put({
        url: '/gold-set/documents/{key}/label',
        path: { key: documentKey },
        body: label,
        throwOnError: true,
      })
      return response.data as GoldSetLabel
    },
    onSuccess: (savedLabel, { documentKey, label }) => {
      queryClient.setQueryData<GoldSetDocument>(
        GOLD_SET_QUERY_KEYS.document(documentKey, label.pass),
        (current) => current && { ...current, status: 'done_by_me', my_label: savedLabel },
      )
      // Applied before the refetch lands so progress and "next to do" are right in between.
      queryClient.setQueryData<GoldSetQueue>(GOLD_SET_QUERY_KEYS.queue(label.pass), (current) => {
        const item = current?.items.find(({ short_key }) => short_key === documentKey)
        if (!current || !item || item.status === 'done_by_me') return current
        return {
          ...current,
          done: item.status === 'todo' ? current.done + 1 : current.done,
          done_by_me: current.done_by_me + 1,
          items: current.items.map((entry) =>
            entry.short_key === documentKey ? { ...entry, status: 'done_by_me' } : entry,
          ),
        }
      })
      queryClient.invalidateQueries({ queryKey: GOLD_SET_QUERY_KEYS.queue(label.pass) })
    },
  })
}
